import type { BookedSpot } from './bookedSpotNames';

export type { BookedSpot };

/** Accept snake_case or camelCase booked-spot fields from API wire payloads. */
export type WireOrCamelBookedSpotFields = {
  bookedSpotNames?: string[] | string;
  booked_spot_names?: string[] | string;
  bookedSpots?: BookedSpot[];
  booked_spots?: BookedSpot[];
};

export type WireOrCamelGuestBookings = {
  guestBookings?: number;
  guest_bookings?: number;
};

export type SpotBookingModelObj = {
  _id?: string;
  name?: string;
  image_url?: string;
  type?: string;
  program_obj?: { room_map_image_url?: string; categories?: string[] };
  layout_id?: string;
  duration?: number | string;
};

export type SpotBookingBookingLike = WireOrCamelBookedSpotFields &
  WireOrCamelGuestBookings & {
    id?: string;
    _id?: string;
    branchId?: string;
    branch_id?: string;
    timeStart?: number | string;
    time_start?: number | string;
    timeSlotId?: string;
    time_slot_id?: string;
    model?: string;
    modelObj?: SpotBookingModelObj;
    model_obj?: SpotBookingModelObj;
    layoutId?: string;
    layout_id?: string;
    duration?: number | string;
    memberBookedSpotName?: string;
  };

export type BuildChangeSpotInteractiveBookingOptions = {
  timezone: string;
  timeFormat: string;
  eventTimeSlotId?: string;
};

export type ChangeSpotInteractiveBookingParams = {
  bookingId: string;
  branchId: string;
  title?: string;
  image: string;
  modelId?: string;
  layoutId?: string;
  spotBookingRoomMapLink: string | null;
  guestBookings: number;
  day: string;
  from: string;
  until: string;
  id: string;
  isQuickBook: boolean;
  eventType: string;
};

/** Route / navigation params shared by member app and webportal interactive spot booking. */
export type InteractiveSpotBookingParamsLike = WireOrCamelBookedSpotFields & {
  isChangeSpotFlow?: boolean;
  /**
   * Set by the app when the change-spot flow targets a booking whose member currently holds no
   * spot. Signals that an empty previous spot is a first assignment (reserve without
   * `old_spot_name`), not a failed swap, so the missing-previous-spot guard must not block.
   *
   * Invariant: when true, the member holds no spot, so `currentBookedSpotName`,
   * `memberBookedSpotName` and the member slot of `bookedSpots` / `bookedSpotNames` must all be
   * empty. See `isFirstSpotAssignment` in `spotBookingLogic` for the qualifying booking shapes.
   */
  isFirstSpotAssignment?: boolean;
  previousSpotName?: string;
  currentBookedSpotName?: string;
  memberBookedSpotName?: string;
  bookSpotPayload?: string;
  spotDisplayLabel?: string;
  type?: string;
  action?: string;
  title?: string;
};

/** Params passed into reserve meta after executeBookSpot builds the POST payload. */
export type BookSpotReserveParams = InteractiveSpotBookingParamsLike & {
  bookSpotPayload: string;
  spotDisplayLabel?: string;
  isChangeSpotFlow?: true;
};

/** `delay` only ever appears alongside `trigger: 'Success'` (matches spotBookingLogic's BOOK_SPOT_SUCCESS_TRIGGER); no trigger means no delay either. */
export type BookSpotReserveMeta<
  TParams extends InteractiveSpotBookingParamsLike = InteractiveSpotBookingParamsLike
> =
  | { trigger: 'Success'; delay?: number; params: TParams }
  | { trigger?: undefined; params: TParams };

export type LastBookSpotContext = {
  timeSlotId: string;
  spotTypePrefix: string;
  spotNumber: string;
  /**
   * Friends mode only: how many slots the reserve covers (member + friends). Left undefined by the
   * single-spot flow, so its absence marks a solo booking in the funnel.
   */
  slotCount?: number;
};

export type SpotBookingCtaMeta = {
  timeSlotId: string;
  /** Solo flow: always 1. Friends flow: filled slots (member + friends) at CTA time. */
  selectedSpotCount: number;
};

export type SpotTapMeta = {
  timeSlotId: string;
  spotTypePrefix: string;
  spotNumber: string;
  isBookable: boolean;
};

export type LastChangeSpotContext = {
  timeSlotId: string;
  previousSpotId: string;
  spotId: string;
};

/**
 * Why executeBookSpot aborted before reserve (for analytics / UI).
 * `INCOMPLETE_FRIEND_SELECTION` is friends-mode only (`executeBookSpotWithFriends`): at least one
 * slot is empty or holds a spot with no usable name, so no partial reserve is sent.
 */
export const MissingSpotFailureReason = {
  MISSING_SPOT_ID: 'missing_spot_id',
  MISSING_PREVIOUS_SPOT_ID: 'missing_previous_spot_id',
  INCOMPLETE_FRIEND_SELECTION: 'incomplete_friend_selection'
} as const;

export type MissingSpotFailureReason =
  (typeof MissingSpotFailureReason)[keyof typeof MissingSpotFailureReason];

export type SpotIdentityLike = {
  spot_id?: string | number;
  id?: string | number;
  spot_name?: string;
  spotName?: string;
};
